import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Sparkles, Heart, Shield, Download, Clock } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TrustBadges } from '../components/TrustBadges';
import { TestimonialCard } from '../components/TestimonialCard';
import { BonusCard } from '../components/BonusCard';
import { UpsellSection } from '../components/UpsellSection';
import { FAQSection } from '../components/FAQSection';
import { InsidePagesSection } from '../components/InsidePagesSection';
// Image Constants
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/i6JrKV1Sg5TjNB7hZXpAae/Diseno_sin_titulo_(5).png';
const BONUS_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/qdiNoaaXimbXcRAgtHgPi9/2680219e-6ed8-489e-a8b1-0760e015a318.png';
const BONUS_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/fhBBoauvADiQ7G4y8FCpkk/099aec3e-228a-44e3-9023-18a0963baeb9.png';
const BONUS_3_IMAGE = 'https://cdn.magicpatterns.com/uploads/iVLqbDfnU2T5iUg2psietn/ff74080a-95dc-4fbf-97fc-49f41fd94123.png';
const BONUS_4_IMAGE = 'https://cdn.magicpatterns.com/uploads/3XnRUadWyiR9WSWw2Rf3DX/396b1e9e-6cbb-4b4e-9f31-d15dfeb9636c.png';
const BONUS_5_IMAGE = 'https://cdn.magicpatterns.com/uploads/eoqbFREMnVbcSPRshhb6U9/ChatGPT_Image_15_ene_2026,_20_16_10.png';
const MOBILE_MOCKUP = 'https://cdn.magicpatterns.com/uploads/djBYDUx89vgmAgbXNY751u/Sabes_que_algo_no_esta_bien._Pero_seguis_igual_porque_frenar_da_miedo_cuando_todo_depende_de_vos..png';
const CHECKOUT_URL = 'https://checkout.keybookpro.com/cart/42793771139135:1';
export function EstresEnPausaPage() {
  // Meta Pixel Integration
  useEffect(() => {
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '817879127936181');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=817879127936181&ev=PageView&noscript=1"
    />`;
    document.body.appendChild(noscript);
  }, []);
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-blue-50/30 via-white to-pink-50/20 font-sans text-gray-700 selection:bg-teal-200 selection:text-teal-900" data-id="element-108">
      <PurchaseNotification data-id="element-109" />

      {/* Enhanced Hero Section with CTA */}
      <header className="relative overflow-hidden pt-16 pb-24 px-4" data-id="element-110">
        {/* Warm gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-sky-100/60 via-white to-teal-100/40" data-id="element-111" />

        {/* Organic decorative elements */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-teal-200/30 to-transparent rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3" data-id="element-112" />
        <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-gradient-to-bl from-sky-200/25 to-transparent rounded-full blur-3xl translate-x-1/3" data-id="element-113" />
        <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-gradient-to-tr from-emerald-200/20 to-transparent rounded-full blur-3xl translate-y-1/2" data-id="element-114" />

        {/* Subtle organic shapes */}
        <svg className="absolute top-10 left-10 w-32 h-32 text-teal-300/10" viewBox="0 0 100 100" data-id="element-115">
          <path d="M50,10 Q80,30 90,50 Q80,70 50,90 Q20,70 10,50 Q20,30 50,10" fill="currentColor" data-id="element-116" />
        </svg>
        <svg className="absolute bottom-20 right-20 w-40 h-40 text-sky-300/10" viewBox="0 0 100 100" data-id="element-117">
          <circle cx="50" cy="50" r="40" fill="currentColor" data-id="element-118" />
        </svg>

        {/* Leaf decorations */}
        <div className="absolute top-1/4 right-1/4 opacity-10" data-id="element-119">
          <Heart className="w-16 h-16 text-teal-500" data-id="element-120" />
        </div>
        <div className="absolute bottom-1/3 left-1/4 opacity-10 rotate-45" data-id="element-121">
          <Heart className="w-12 h-12 text-sky-500" data-id="element-122" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-123">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-124">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-semibold text-teal-700 mb-6 shadow-sm border border-teal-100/50" data-id="element-125">
              <Sparkles className="w-4 h-4" data-id="element-126" />
              Para mujeres que necesitan una pausa sin culpa
            </div>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-700 mb-4 leading-tight px-4" data-id="element-127">
              Estás cansada… y no es porque seas débil
            </h1>

            {/* Emotional micro-copy */}
            <p className="text-lg md:text-xl text-gray-500 font-medium mb-8 italic" data-id="element-128">
              No estás rota. Estás cansada.
            </p>

            {/* Hero CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="mb-10" data-id="element-129">
              <button onClick={scrollToPurchase} className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white text-lg md:text-xl font-semibold py-4 px-10 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-3" data-id="element-130">
                Empezar mi pausa hoy
              </button>
              <div className="flex items-center justify-center gap-4 text-xs md:text-sm text-gray-500" data-id="element-131">
                <span className="flex items-center gap-1.5" data-id="element-132">
                  <Clock className="w-4 h-4" data-id="element-133" />
                  Acceso inmediato
                </span>
                <span data-id="element-134">·</span>
                <span className="flex items-center gap-1.5" data-id="element-135">
                  <Download className="w-4 h-4" data-id="element-136" />
                  Descarga digital
                </span>
                <span data-id="element-137">·</span>
                <span className="flex items-center gap-1.5" data-id="element-138">
                  <Shield className="w-4 h-4" data-id="element-139" />
                  Garantía de 7 días
                </span>
              </div>
            </motion.div>

            {/* Book mockup with enhanced integration */}
            <div className="flex justify-center mb-8 cursor-pointer relative" onClick={scrollToPurchase} data-id="element-140">
              {/* Soft base/shadow for book */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-gradient-to-b from-teal-200/30 to-transparent blur-2xl rounded-full" data-id="element-141" />

              <motion.div whileHover={{
              scale: 1.05,
              y: -5
            }} transition={{
              duration: 0.3
            }} className="relative" data-id="element-142">
                <img src={MAIN_IMAGE} alt="Estrés en Pausa Libro" className="w-full max-w-md drop-shadow-2xl rounded-lg relative z-10" data-id="element-143" />
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-teal-400/20 to-transparent blur-xl rounded-lg -z-10" data-id="element-144" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Problem Section - Condensed */}
      <section className="py-12 px-4 max-w-3xl mx-auto" data-id="element-145">
        <div className="space-y-8 text-lg leading-relaxed text-gray-600" data-id="element-146">
          <div className="space-y-4" data-id="element-147">
            <p data-id="element-148">
              Hay un cansancio que no se cura durmiendo. Que no se arregla con
              café ni con "organizarte mejor".
            </p>
            <p className="font-semibold text-xl text-gray-700" data-id="element-149">
              Por fuera funcionas. Por dentro… estás agotada.
            </p>
          </div>

          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-teal-100" data-id="element-150">
            <h3 className="font-bold text-xl mb-5 text-teal-700" data-id="element-151">
              Si últimamente te pasa algo como esto:
            </h3>
            <ul className="space-y-3" data-id="element-152">
              {['Te acuestas cansada y te levantas igual', 'Tu mente no se apaga', 'Explotas por cosas pequeñas… y después te culpas', 'Vives en piloto automático', 'Pensás: "Antes yo no era así…"'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-153">
                  <div className="mt-1.5 w-2 h-2 rounded-full bg-rose-400 flex-shrink-0" data-id="element-154" />
                  <span className="text-gray-600" data-id="element-155">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-6 rounded-2xl text-center" data-id="element-156">
            <p className="text-xl font-bold text-teal-600 mb-2" data-id="element-157">
              No estás rota. No estás exagerando.
            </p>
            <p className="text-gray-600" data-id="element-158">
              Estás agotada de ser fuerte todo el tiempo.
            </p>
          </div>

          <div className="space-y-4" data-id="element-159">
            <p className="font-medium text-gray-700" data-id="element-160">
              Tal vez intentaste solucionarlo con:
            </p>
            <ul className="grid md:grid-cols-2 gap-3 text-base" data-id="element-161">
              {['Meditación (que no lograste sostener)', 'Videos de YouTube', 'Frases motivacionales', '"Organizarte mejor"', 'Aguantar y seguir'].map((item, i) => <li key={i} className="flex items-center gap-2 bg-white p-3 rounded-lg border border-gray-100" data-id="element-162">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400" data-id="element-163" />
                  <span className="text-gray-600" data-id="element-164">{item}</span>
                </li>)}
            </ul>
          </div>

          <p className="text-center italic text-gray-500" data-id="element-165">
            Pero casi siempre pasa lo mismo: funciona dos días… y volvés al
            mismo punto.
          </p>

          <div className="bg-white p-6 rounded-xl border-l-4 border-teal-500 shadow-sm" data-id="element-166">
            <p className="font-semibold text-gray-700 mb-2" data-id="element-167">
              El problema no eres tú. Es el enfoque.
            </p>
            <p className="text-gray-600" data-id="element-168">
              Esas soluciones te piden lo último que hoy te falta: más tiempo,
              más fuerza de voluntad, más exigencia.
            </p>
          </div>
        </div>
      </section>

      {/* Root Cause Section - Condensed */}
      <section className="py-12 px-4 bg-gradient-to-br from-teal-50/50 to-blue-50/50" data-id="element-169">
        <div className="max-w-3xl mx-auto" data-id="element-170">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-700 mb-6 text-center" data-id="element-171">
            La raíz oculta del estrés
          </h2>
          <div className="space-y-6 text-lg text-gray-600" data-id="element-172">
            <p className="text-center max-w-2xl mx-auto" data-id="element-173">
              El estrés no te domina por falta de tiempo. Te domina porque vivís
              en un estado constante de exigencia interna.
            </p>

            <div className="grid md:grid-cols-3 gap-4 my-8" data-id="element-174">
              {[{
              text: 'Descansás, pero tu mente sigue trabajando'
            }, {
              text: 'Te detenés, pero aparece la culpa'
            }, {
              text: 'Tenés un momento libre… y te sentís "atrasada"'
            }].map((item, i) => <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-teal-100 text-center" data-id="element-175">
                  <p className="text-sm text-gray-600" data-id="element-176">{item.text}</p>
                </div>)}
            </div>

            <div className="bg-white p-6 rounded-xl border-l-4 border-teal-500 shadow-sm max-w-2xl mx-auto" data-id="element-177">
              <p className="italic text-gray-600 text-center" data-id="element-178">
                Es como manejar con el freno de mano puesto: avanzás, sí… pero
                el desgaste es brutal.
              </p>
            </div>

            <p className="text-center max-w-2xl mx-auto" data-id="element-179">
              Tu mente es como un navegador con demasiadas pestañas abiertas. No
              necesitás "una cosa más". Necesitás aprender a cerrar las que ya
              no sirven.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section - Condensed with Mobile Mockup */}
      <section className="py-16 px-4 max-w-5xl mx-auto" data-id="element-180">
        <div className="text-center mb-10" data-id="element-181">
          <span className="text-teal-600 font-semibold tracking-wider uppercase text-sm" data-id="element-182">
            Presentación
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-700 mt-2 mb-4" data-id="element-183">
            Estrés en Pausa
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-184">
            Un ebook para mujeres agotadas de ser fuertes todo el tiempo.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10 items-center mb-12" data-id="element-185">
          <div className="cursor-pointer order-2 md:order-1" onClick={scrollToPurchase} data-id="element-186">
            <motion.img whileHover={{
            scale: 1.05
          }} transition={{
            duration: 0.3
          }} src={MOBILE_MOCKUP} alt="Estrés en Pausa en móvil" className="w-full max-w-sm mx-auto rounded-2xl shadow-2xl" data-id="element-187" />
          </div>
          <div className="space-y-5 text-base md:text-lg text-gray-600 order-1 md:order-2" data-id="element-188">
            <p className="font-semibold text-teal-600 text-xl" data-id="element-189">
              No es un ebook para exigirte más. Es un ebook para enseñarte a
              pausar.
            </p>

            <p data-id="element-190">
              Te ayuda a bajar el estrés y recuperar el control de tu vida sin
              culpa, con herramientas simples que podés aplicar incluso cuando
              sentís que no te da el tiempo.
            </p>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-5 rounded-xl" data-id="element-191">
              <p className="font-semibold text-gray-700 mb-3" data-id="element-192">
                ¿Qué hace diferente a Estrés en Pausa?
              </p>
              <p className="text-gray-600" data-id="element-193">
                No te pide que cambies tu vida. Te enseña a cambiar cómo la
                estás cargando por dentro, a través de micro-pausas conscientes
                sin culpa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Condensed */}
      <section className="py-12 px-4 bg-white" data-id="element-194">
        <div className="max-w-3xl mx-auto" data-id="element-195">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-700 mb-8 text-center" data-id="element-196">
            ¿Qué vas a lograr?
          </h2>

          <div className="grid md:grid-cols-2 gap-4" data-id="element-197">
            {['Una mente más tranquila y clara', 'Menos ansiedad acumulada', 'Más calma al responder', 'Más presencia con tu familia', 'Más descanso real', 'Más control interno'].map((benefit, i) => <div key={i} className="flex items-center gap-3 p-4 bg-teal-50/50 rounded-lg border border-teal-100" data-id="element-198">
                <div className="bg-teal-100 p-2 rounded-full flex-shrink-0" data-id="element-199">
                  <Check className="w-4 h-4 text-teal-600" data-id="element-200" />
                </div>
                <span className="text-base font-medium text-gray-700" data-id="element-201">
                  {benefit}
                </span>
              </div>)}
          </div>

          <p className="text-center mt-8 text-lg font-medium text-teal-600" data-id="element-202">
            No se trata de hacer más. Se trata de volver a sentirte vos.
          </p>
        </div>
      </section>

      {/* Who is this for - Condensed */}
      <section className="py-12 px-4 max-w-3xl mx-auto" data-id="element-203">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-700 mb-8 text-center" data-id="element-204">
          Este libro es para vos si…
        </h2>
        <div className="grid md:grid-cols-2 gap-3" data-id="element-205">
          {['Estás cansada de ser la fuerte', 'Sentís que vivís corriendo', 'Descansás, pero no te recuperás', 'Tu mente no se apaga', 'Vivís con culpa por descansar', 'Querés volver a vos sin huir'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-100" data-id="element-206">
              <div className="mt-1 w-2 h-2 rounded-full bg-teal-500 flex-shrink-0" data-id="element-207" />
              <span className="text-gray-600" data-id="element-208">{item}</span>
            </div>)}
        </div>

        <div className="mt-10 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-2xl text-center shadow-sm" data-id="element-209">
          <h3 className="text-xl md:text-2xl font-bold text-gray-700 mb-3" data-id="element-210">
            La promesa central
          </h3>
          <p className="text-lg text-gray-600" data-id="element-211">
            Podés bajar el estrés y recuperar el control sin cambiar quién sos
            ni exigirte más.
          </p>
        </div>
      </section>

      {/* Inside Pages Section - MOVED HERE (after "who is this for", before purchase) */}
      <InsidePagesSection data-id="element-212" />

      {/* CTA Section */}
      <section className="py-12 text-center bg-gradient-to-b from-white to-blue-50/20" data-id="element-213">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-700 mb-4" data-id="element-214">
          Hacé una pausa hoy
        </h2>
        <div className="max-w-xl mx-auto px-4 space-y-3 text-base md:text-lg text-gray-600 mb-8" data-id="element-215">
          <p className="font-semibold text-gray-700" data-id="element-216">
            No podés seguir cargando tu vida de esta manera.
          </p>
          <p data-id="element-217">
            Estrés en Pausa puede ser el primer paso real para recuperar tu
            calma, tu claridad y tu control… sin culpa.
          </p>
        </div>

        <button onClick={scrollToPurchase} className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-xl font-bold py-4 px-12 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1" data-id="element-218">
          Acceder ahora
        </button>
      </section>

      {/* Bonuses & Purchase Section */}
      <section id="purchase-section" className="py-16 px-4 bg-gradient-to-b from-blue-50/20 to-white" data-id="element-219">
        <div className="max-w-5xl mx-auto" data-id="element-220">
          <div className="text-center mb-12" data-id="element-221">
            <h2 className="text-3xl font-bold text-teal-600 mb-2" data-id="element-222">
              BONOS GRATIS POR HOY
            </h2>
            <p className="text-gray-500" data-id="element-223">
              Al adquirir Estrés en Pausa, recibís estos regalos exclusivos
            </p>
          </div>

          {/* Bonus Grid */}
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 mb-12" data-id="element-224">
            <div className="w-28 md:w-36" data-id="element-225">
              <BonusCard image={BONUS_1_IMAGE} title="Bonus 1" onClick={scrollToPurchase} data-id="element-226" />
            </div>
            <span className="text-3xl text-gray-300 font-light" data-id="element-227">+</span>
            <div className="w-28 md:w-36" data-id="element-228">
              <BonusCard image={BONUS_2_IMAGE} title="Bonus 2" onClick={scrollToPurchase} data-id="element-229" />
            </div>
            <span className="text-3xl text-gray-300 font-light hidden md:block" data-id="element-230">
              +
            </span>
            <div className="w-28 md:w-36" data-id="element-231">
              <BonusCard image={BONUS_3_IMAGE} title="Bonus 3" onClick={scrollToPurchase} data-id="element-232" />
            </div>
            <span className="text-3xl text-gray-300 font-light" data-id="element-233">+</span>
            <div className="w-28 md:w-36" data-id="element-234">
              <BonusCard image={BONUS_4_IMAGE} title="Bonus 4" onClick={scrollToPurchase} data-id="element-235" />
            </div>
            <span className="text-3xl text-gray-300 font-light hidden md:block" data-id="element-236">
              +
            </span>
            <div className="w-28 md:w-36" data-id="element-237">
              <BonusCard image={BONUS_5_IMAGE} title="Bonus 5" onClick={scrollToPurchase} data-id="element-238" />
            </div>
          </div>

          {/* Pricing Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-3xl mx-auto border border-gray-100" data-id="element-239">
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 py-4 text-center text-white font-bold tracking-wider" data-id="element-240">
              OFERTA ESPECIAL
            </div>
            <div className="p-8 md:p-12 text-center" data-id="element-241">
              <div className="mb-2" data-id="element-242">
                <p className="text-sm text-gray-500 mb-2" data-id="element-243">
                  Libro principal + 5 bonos + 3 audios de relajación guiada
                </p>
              </div>
              <div className="mb-8" data-id="element-244">
                <span className="text-6xl font-bold text-gray-700" data-id="element-245">
                  $19.990
                </span>
              </div>

              <a href={CHECKOUT_URL} className="block w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-2xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-8 flex items-center justify-center gap-3" data-id="element-246">
                Acceder ahora a Estrés en Pausa
                <ArrowRight className="w-6 h-6" data-id="element-247" />
              </a>

              <p className="text-gray-500 mb-8" data-id="element-248">
                Hacé clic en el botón y empezá hoy.
              </p>

              <TrustBadges data-id="element-249" />
            </div>
          </div>

          {/* Upsell Section */}
          <UpsellSection data-id="element-250" />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-blue-50/30" data-id="element-251">
        <div className="max-w-6xl mx-auto" data-id="element-252">
          <h2 className="text-3xl font-bold text-center text-gray-700 mb-12" data-id="element-253">
            Lo que dicen otras mujeres como vos
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-254">
            <TestimonialCard name="María González" location="Buenos Aires" age={42} text="Después de años sintiéndome agotada, este libro me enseñó a pausar sin culpa. Ahora duermo mejor y tengo más paciencia con mi familia." data-id="element-255" />
            <TestimonialCard name="Laura Martínez" location="Santiago" age={38} text="No sabía que podía sentirme así de tranquila sin cambiar toda mi vida. Las micro-pausas realmente funcionan." data-id="element-256" />
            <TestimonialCard name="Carolina Rodríguez" location="Bogotá" age={45} text="Llevaba meses sin poder desconectar mi mente. Este método me devolvió la calma que creía perdida." data-id="element-257" />
            <TestimonialCard name="Sofía Fernández" location="Ciudad de México" age={35} text="Finalmente entendí por qué estaba tan cansada. No necesitaba más fuerza, necesitaba aprender a soltar." data-id="element-258" />
            <TestimonialCard name="Patricia Silva" location="Lima" age={41} text="Pensé que era la única que se sentía así. Este libro me mostró que puedo recuperarme sin escapar de mi vida." data-id="element-259" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection data-id="element-260" />

      {/* Final Footer CTA */}
      <footer className="bg-gradient-to-r from-teal-500 to-teal-600 text-white py-12 px-4 text-center" data-id="element-261">
        <div className="max-w-2xl mx-auto" data-id="element-262">
          <h3 className="text-2xl font-bold mb-6" data-id="element-263">
            Porque vos también merecés una vida que se sienta liviana otra vez.
          </h3>
          <button onClick={scrollToPurchase} className="bg-white text-teal-600 font-bold py-3 px-8 rounded-full shadow-lg hover:bg-gray-50 transition-colors" data-id="element-264">
            Empezar mi cambio hoy
          </button>
          <p className="mt-8 text-sm opacity-80" data-id="element-265">
            © {new Date().getFullYear()} Estrés en Pausa. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>
    </div>;
}